import { useEffect, useRef, useState } from 'react';
import { ApiError, prettyPhone, serverClock, useAction } from '@limuzin/kit';
import type { ProofStateView, StartedProofView } from '@limuzin/passenger';
import { checkProof, pollProof, resendProof } from '../api';
import { SmsMessage } from '../icons';
import { useSession } from '../session';
import { BackDisc, Button, ErrorNote, LabelledField } from '../widgets';

/** The digits of the code the gateway texts. */
const CODE_LENGTH = 6;

/**
 * «Подтвердите номер» — the code the gateway texts to prove a number (ADR 0052),
 * the same screen as `apps/rider/lib/features/auth/proof_screen.dart`.
 *
 * The passenger types the code from the SMS; the sixth digit sends it, and the right
 * one hands [onProved] the proof to spend, and the answer it came in — which, for
 * «Регистрация», says whether the number already has an account. In Chrome on
 * Android the code is offered from the SMS itself (WebOTP), and Safari offers it
 * over the keyboard: the field is `one-time-code` for both.
 *
 * Under the field it says what became of the SMS, as the gateway told the server,
 * and how long the code has; «Отправить код ещё раз» opens a minute after a send, or
 * at once if it failed.
 *
 * The caller starts the proof first and hands it over as [started], so a refusal to
 * start — no gateway online — stays on the caller's own screen. [restart] is for
 * «Начать заново», once the proof is over.
 */
export function ProofScreen({
  phone,
  started: first,
  restart,
  onProved,
  onBack,
}: {
  phone: string;
  started: StartedProofView;
  restart: () => Promise<StartedProofView>;
  onProved: (verificationToken: string, answer: ProofStateView) => void | Promise<void>;
  onBack: () => void;
}) {
  const { api } = useSession();
  const [started, setStarted] = useState(first);
  const [state, setState] = useState<ProofStateView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [now, setNow] = useState(() => serverClock.now());
  const input = useRef<HTMLInputElement>(null);
  const proved = useRef(onProved);
  proved.current = onProved;
  // A code typed and one WebOTP offered may both arrive; only one of them may spend it.
  const spent = useRef(false);

  const left = Math.max(0, Math.floor((Date.parse(started.expiresAt) - now) / 1000));
  const status: ProofStateView['status'] =
    state?.status === 'proved'
      ? 'proved'
      : locked || state?.status === 'locked'
        ? 'locked'
        : left === 0
          ? 'expired'
          : 'waiting';
  // The latest word on when it may be sent again: a failed SMS opens it at once.
  const resendAt =
    status === 'proved' || status === 'locked' ? null : (state?.resendAt ?? started.resendAt);
  const resendIn =
    resendAt === null ? null : Math.max(0, Math.ceil((Date.parse(resendAt) - now) / 1000));
  const over = status === 'locked' || (status === 'expired' && resendIn === null);
  const delivery = state?.delivery ?? 'queued';

  // The countdowns, between the server's answers — to moments it set, so by its clock.
  useEffect(() => {
    if (status === 'proved' || status === 'locked') return;
    const timer = window.setInterval(() => setNow(serverClock.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  // The long-poll for news of the SMS, until it has arrived or failed. A resend
  // gives [started] a new identity, and with it a poll of its own.
  useEffect(() => {
    let alive = true;
    let known: string | undefined;
    setState(null);
    setError(null);
    void (async () => {
      while (alive) {
        try {
          const answer = await pollProof(api, started.id, known);
          if (!alive) return;
          known = answer.key;
          setState(answer);
          if (answer.status !== 'waiting') return;
          if (answer.delivery === 'delivered' || answer.delivery === 'failed') return;
        } catch (caught) {
          if (!alive) return;
          if (caught instanceof ApiError && caught.code === 'offline') {
            await new Promise((resolve) => window.setTimeout(resolve, 2000));
            continue;
          }
          setError(caught instanceof ApiError ? caught : new ApiError('unknown', 'Что-то пошло не так'));
          return;
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, started]);

  const submit = useAction(async (typed: string) => {
    if (typed.length !== CODE_LENGTH || spent.current) return;
    setCodeError(null);
    try {
      const answer = await checkProof(api, started.id, typed);
      setState(answer);
      if (answer.status === 'proved' && !spent.current) {
        spent.current = true;
        await proved.current(answer.verificationToken!, answer);
      }
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught;
      if (caught.code === 'proof_locked') setLocked(true);
      setCodeError(
        caught.code === 'offline'
          ? 'Нет связи. Проверьте интернет и попробуйте ещё раз'
          : caught.message,
      );
      // A wrong code is typed again from the start, not edited.
      setCode('');
      input.current?.focus();
    }
  });

  // WebOTP: Chrome on Android reads the code out of the SMS, whose last line names
  // this site, and offers it. Asked once per send; a resend asks again.
  useEffect(() => {
    if (!('OTPCredential' in window)) return;
    const abort = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: abort.signal } as CredentialRequestOptions)
      .then((credential) => {
        const offered = (credential as { code?: string } | null)?.code;
        if (!offered) return;
        setCode(offered);
        void submit.run(offered);
      })
      .catch(() => undefined);
    return () => abort.abort();
  }, [started]);

  const resend = useAction(async () => {
    setCode('');
    setCodeError(null);
    try {
      setStarted(await resendProof(api, started.id));
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'offline') {
        throw new ApiError('offline', 'Нет связи. Проверьте интернет и попробуйте ещё раз');
      }
      throw caught;
    }
    setNow(serverClock.now());
    input.current?.focus();
  });

  const again = useAction(async () => {
    setCode('');
    setCodeError(null);
    setLocked(false);
    setStarted(await restart());
    setNow(serverClock.now());
    input.current?.focus();
  });

  const typed = (value: string) => {
    const digits = codeDigits(value);
    setCode(digits);
    if (digits.length === CODE_LENGTH) void submit.run(digits);
  };

  const clock = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

  const line = proofLine(status, delivery);

  return (
    <div className="proof-scroll">
      <div className="column proof">
        <div style={{ paddingTop: 'calc(24px + var(--safe-top))' }}>
          <BackDisc onClick={onBack} />
          <h1 className="proof-title">Подтвердите номер</h1>
          <p className="body proof-lead">
            Мы отправили SMS с кодом на <strong>{prettyPhone(started.phone ?? phone)}</strong>.
            Введите его ниже.
          </p>
        </div>

        <SmsPicture />

        <div className="proof-code">
          <LabelledField
            label="Код из SMS"
            inputRef={input}
            className="proof-code-input"
            value={code}
            onChange={(event) => typed(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            placeholder="••••••"
            autoFocus
            disabled={over || status === 'expired' || submit.pending}
          />
        </div>
        <ErrorNote error={codeError} />
        <p className={`proof-line ${line.bad ? 'bad' : ''}`}>
          {line.text}
          {status === 'waiting' && delivery !== 'failed' && (
            <>
              {' · код действует ещё '}
              <strong>{clock(left)}</strong>
            </>
          )}
        </p>
        <ErrorNote error={error ?? resend.error ?? again.error ?? submit.error} />

        <div className="grow" style={{ minHeight: 14 }} />
        <div style={{ paddingBottom: 20 }}>
          {over ? (
            <Button busy={again.pending} onClick={() => void again.run()}>
              Начать заново
            </Button>
          ) : (
            <>
              <Button
                busy={submit.pending}
                disabled={status !== 'waiting' || code.length !== CODE_LENGTH}
                onClick={() => void submit.run(code)}
              >
                Подтвердить
              </Button>
              {resendIn !== null && (
                <p style={{ textAlign: 'center', padding: '14px 10px 0', margin: 0 }}>
                  <button
                    type="button"
                    className="linkish"
                    disabled={resendIn > 0 || resend.pending}
                    onClick={() => void resend.run()}
                  >
                    {resendIn > 0
                      ? `Отправить ещё раз через ${clock(resendIn)}`
                      : 'Отправить код ещё раз'}
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What the line under the code says: what became of the SMS, or that the code is
 * done with. Red when there is something to do about it.
 */
export function proofLine(
  status: ProofStateView['status'],
  delivery: ProofStateView['delivery'],
): { text: string; bad: boolean } {
  if (status === 'expired') return { text: 'Код устарел. Отправьте новый', bad: true };
  if (status === 'locked') return { text: 'Код больше не действует', bad: true };
  if (status === 'proved') return { text: 'Номер подтверждён', bad: false };
  switch (delivery) {
    case 'failed':
      return { text: 'Не удалось отправить SMS. Отправьте ещё раз', bad: true };
    case 'sent':
      return { text: 'SMS отправлено', bad: false };
    case 'delivered':
      return { text: 'SMS доставлено', bad: false };
    default:
      return { text: 'Отправляем SMS…', bad: false };
  }
}

/** The digits of what was typed or pasted, six at most: «482 731» is a code. */
export function codeDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

/**
 * A phone with a message on it, and a speech bubble behind with the sound of it
 * arriving: the SMS on its way. Drawn to the design's picture, the phone running
 * off its foot — the same drawing as `_SmsPainter` in the mobile apps.
 */
function SmsPicture() {
  return (
    <svg className="proof-picture" viewBox="0 0 230 136" aria-hidden="true">
      <ellipse cx="68" cy="61" rx="48" ry="42" fill="var(--tint)" />
      <path d="M60 98 77.5 116V96z" fill="var(--tint)" />
      <path
        d="m77 37 8 10M60 49l19.5 7M60 75l18.5-6M75 88l7.5-7.5"
        stroke="var(--accent)"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path
        d="M93.0 150.0L110.1 21.9Q112.0 8.0 126.0 8.7L195.0 12.3Q209.0 13.0 207.2 26.9L191.0 150.0"
        fill="var(--card)"
        stroke="color-mix(in srgb, var(--accent) 65%, var(--muted))"
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <g transform="translate(120 54)" color="var(--accent)">
        <SmsMessage stroke={3.5} />
      </g>
    </svg>
  );
}
